import React, {useEffect, useRef, useState, useCallback} from 'react';
import {Dialog, Spinner, Tag, Button, Icon} from '@blueprintjs/core';
import styled from 'styled-components';
import Hls from 'hls.js';

import {device} from '/src/services/iot-api';
import {errorToast} from '/src/utils/toaster';

const STREAM_LIFETIME_MS = 3 * 60 * 1000;
const RENEW_BEFORE_MS = 15 * 1000;
const RENEW_AT_MS = STREAM_LIFETIME_MS - RENEW_BEFORE_MS;

const VideoContainer = styled.div`
    position: relative;
    width: 100%;
    background: #000;
    min-height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;

    video {
        width: 100%;
        max-height: 70vh;
    }

    &:fullscreen {
        min-height: 0;

        video {
            max-height: 100%;
            height: 100%;
            object-fit: contain;
        }
    }

    &:hover .video-controls {
        opacity: 1;
    }
`;

const VideoControls = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
    padding: 8px 12px;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
    opacity: 0;
    transition: opacity 0.25s ease;

    button {
        background: none;
        border: none;
        color: rgba(255, 255, 255, 0.85);
        cursor: pointer;
        padding: 6px;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
            color: #fff;
            background: rgba(255, 255, 255, 0.15);
        }
    }

    svg {
        fill: currentColor;
    }
`;

const StatusBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: #1c2127;
    border-top: 1px solid #394b59;
`;

const Timer = styled.span`
    font-variant-numeric: tabular-nums;
    color: #a7b6c2;
    font-size: 12px;
`;

const LiveStreamOverlay = ({isOpen, onClose, deviceName, recid, recName}) => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const renewTimerRef = useRef(null);
    const startTimeRef = useRef(null);
    const [status, setStatus] = useState('starting'); // starting | loading | playing | error | stopped
    const [countdown, setCountdown] = useState(null);
    const countdownRef = useRef(null);
    const containerRef = useRef(null);

    const toggleFullscreen = useCallback(() => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            containerRef.current?.requestFullscreen();
        }
    }, []);

    const startStream = useCallback((retryDelay = 2000, silent = false) => {
        if (!silent) setStatus('starting');

        const attempt = (delay) =>
            device.command('LiveStreamCommand', deviceName, {params: {recid, streaming: true}}, 'update')
                .then(resp => {
                    const data = resp.payload?.resp;
                    if (data?.error) throw new Error(data.error);
                    if (!data?.hls_url) {
                        if (delay > 15000) throw new Error('Device did not start streaming');
                        return new Promise(resolve => setTimeout(resolve, delay))
                            .then(() => attempt(delay * 2));
                    }
                    return data.hls_url;
                });

        return attempt(retryDelay);
    }, [deviceName, recid]);

    const attachHls = useCallback((url) => {
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        const video = videoRef.current;
        if (!video) return;

        if (Hls.isSupported()) {
            const hls = new Hls({
                enableWorker: true,
                lowLatencyMode: true,
                liveSyncDurationCount: 2,
                liveMaxLatencyDurationCount: 5,
                maxLiveSyncPlaybackRate: 1.5,
            });
            hlsRef.current = hls;

            hls.loadSource(url);
            hls.attachMedia(video);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                setStatus('playing');
                video.play().catch(() => {});
            });

            hls.on(Hls.Events.ERROR, (_event, data) => {
                if (data.fatal) {
                    if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                        // Network errors during loading are expected while stream spins up
                        if (status === 'starting' || status === 'loading') {
                            setStatus('loading');
                            hls.startLoad();
                        } else {
                            setStatus('error');
                        }
                    } else {
                        setStatus('error');
                    }
                }
            });

            setStatus('loading');
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            // Safari native HLS
            video.src = url;
            video.addEventListener('loadedmetadata', () => {
                setStatus('playing');
                video.play().catch(() => {});
            });
            setStatus('loading');
        } else {
            setStatus('error');
            errorToast({message: 'HLS playback is not supported in this browser'});
        }
    }, []);

    const scheduleRenew = useCallback(() => {
        if (renewTimerRef.current) clearTimeout(renewTimerRef.current);
        startTimeRef.current = Date.now();

        renewTimerRef.current = setTimeout(() => {
            startStream(2000, true).then(url => {
                // Seamless renew — just swap the source on the existing HLS instance
                if (hlsRef.current) {
                    hlsRef.current.loadSource(url);
                } else {
                    attachHls(url);
                }
                scheduleRenew();
            }).catch(err => {
                console.warn('Failed to renew stream:', err);
                errorToast({message: 'Failed to renew live stream'});
                setStatus('error');
            });
        }, RENEW_AT_MS);
    }, [startStream, attachHls]);

    // Countdown timer
    useEffect(() => {
        if (!isOpen || !startTimeRef.current) return;

        const tick = () => {
            if (!startTimeRef.current) return;
            const elapsed = Date.now() - startTimeRef.current;
            const remaining = Math.max(0, Math.ceil((STREAM_LIFETIME_MS - elapsed) / 1000));
            const mins = Math.floor(remaining / 60);
            const secs = remaining % 60;
            setCountdown(`${mins}:${String(secs).padStart(2, '0')}`);
        };

        tick();
        countdownRef.current = setInterval(tick, 1000);
        return () => clearInterval(countdownRef.current);
    }, [isOpen, status]);

    // Start/stop stream on open/close
    useEffect(() => {
        if (isOpen) {
            startStream().then(url => {
                attachHls(url);
                scheduleRenew();
            }).catch(err => {
                console.warn('Failed to start stream:', err);
                errorToast({message: `Failed to start live stream: ${err?.message ?? err}`});
                setStatus('error');
            });
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
            if (renewTimerRef.current) {
                clearTimeout(renewTimerRef.current);
                renewTimerRef.current = null;
            }
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
                countdownRef.current = null;
            }
            startTimeRef.current = null;
        };
    }, [isOpen]);

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            title={`Live Stream — ${recName || recid}`}
            icon="eye-open"
            style={{width: 'min(960px, 90vw)', paddingBottom: 0}}
            canOutsideClickClose={false}
        >
            <VideoContainer ref={containerRef}>
                <video ref={videoRef} muted autoPlay playsInline />
                <VideoControls className="video-controls">
                    <button onClick={toggleFullscreen} title="Fullscreen">
                        <svg width="18" height="18" viewBox="0 0 24 24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>
                    </button>
                </VideoControls>
                {(status === 'starting' || status === 'loading') && (
                    <div style={{position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12}}>
                        <Spinner size={40} />
                        <Tag minimal intent="primary">
                            {status === 'starting' ? 'Starting stream...' : 'Waiting for data...'}
                        </Tag>
                    </div>
                )}
                {status === 'error' && (
                    <div style={{position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12}}>
                        <Icon icon="error" size={40} intent="danger" />
                        <Tag minimal intent="danger">Stream unavailable</Tag>
                        <Button
                            small
                            intent="primary"
                            text="Retry"
                            onClick={() => {
                                startStream().then(url => {
                                    attachHls(url);
                                    scheduleRenew();
                                }).catch(err => {
                                    errorToast({message: `Failed to restart stream: ${err?.message ?? err}`});
                                    setStatus('error');
                                });
                            }}
                        />
                    </div>
                )}
            </VideoContainer>
            <StatusBar>
                <Tag intent={status === 'playing' ? 'success' : status === 'error' ? 'danger' : 'warning'} minimal round>
                    {status === 'playing' ? 'LIVE' : status === 'error' ? 'ERROR' : 'CONNECTING'}
                </Tag>
                {countdown && <Timer>Renews in {countdown}</Timer>}
            </StatusBar>
        </Dialog>
    );
};

export {LiveStreamOverlay};
